import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouteObject,
  useLocation,
  useNavigationType,
} from 'react-router';

const createBrowserRouterWithSentry =
  (routes: RouteObject[], opts?: { basename: string }) => () =>
    Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(routes, opts);

// IndexedDB connection errors are browser/OS-level and self-healing —
// RxDbConnectionService auto-retries via its onClose handler. Amplitude also
// uses IndexedDB internally. Neither case represents a code bug, so we drop
// them before they reach Sentry.
const isTransientIndexedDBError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'DatabaseClosedError' ||
    error.message.includes('Connection to Indexed Database server lost') ||
    error.message.includes(
      'internal error was encountered in the Indexed Database server'
    )
  );
};

// The offline client's RxDB writes reject with an OfflineSyncError carrying the
// collection, operation, doc ids and underlying cause — everything needed to
// debug a failed write. Sentry serializes only name/message/stack, so those
// fields are promoted onto the event below. Matched by name rather than
// imported: the class lives in the client, which this lib must not depend on.
type OfflineSyncErrorFields = {
  operation?: string;
  collectionName?: string;
  docs?: unknown;
};

const isOfflineSyncError = (
  error: unknown
): error is Error & OfflineSyncErrorFields =>
  error instanceof Error && error.name === 'OfflineSyncError';

// Three causes hide behind one wrapper and want different fixes: a lost write
// race, a dead IndexedDB connection, and a document that should have been there.
const offlineWriteFailure = (error: Error & OfflineSyncErrorFields): string => {
  const cause = error.cause;

  if (error.operation === 'Find for patch') {
    return 'missing';
  }
  if (cause instanceof Error && cause.name.includes('CONFLICT')) {
    return 'conflict';
  }
  if (isTransientIndexedDBError(cause)) {
    return 'storage';
  }
  return 'unknown';
};

const setupErrorCapture = (options: {
  sentryDsn: string;
  mode: string;
  version: string;
}) => {
  if (!options.sentryDsn) {
    console.warn('Sentry DSN not provided. Error capture is disabled.');
    return;
  }

  Sentry.init({
    dsn: options.sentryDsn,
    environment: options.mode || 'development',
    replaysSessionSampleRate: options.mode === 'production' ? 1.0 : 0.0,
    replaysOnErrorSampleRate: options.mode === 'production' ? 1.0 : 0.0,
    release: options.version,
    tracesSampleRate: 1.0,
    allowUrls: [/https?:\/\/.*\.airship\.us/],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (isTransientIndexedDBError(error)) {
        return null;
      }
      if (isOfflineSyncError(error)) {
        return {
          ...event,
          tags: {
            ...event.tags,
            'error.type': 'offline-write',
            'offline.collection': error.collectionName || 'UNKNOWN',
            'offline.operation': error.operation || 'UNKNOWN',
            'offline.failure': offlineWriteFailure(error),
          },
          contexts: {
            ...event.contexts,
            'Offline Write': {
              collection: error.collectionName,
              operation: error.operation,
              docs: error.docs,
            },
          },
        };
      }
      return event;
    },
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });
  Sentry.setTags({
    version: options.version,
  });
};

interface User {
  email: string;
  username: string;
  userId: string;
  userRole: 'OWNER' | 'TECHNICIAN' | 'AIRSHIP_ADMIN';
  organizationId: string;
}

const setErrorCaptureUser = (user: User) => {
  Sentry.setUser({
    email: user.email,
    id: user.userId,
    username: user.username,
  });
  Sentry.setTags({
    organizationId: user.organizationId,
    userRole: user.userRole,
  });
};

const addSentryBreadcrumb = (breadcrumb: {
  message: string;
  category: string;
  level: 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}) => Sentry.addBreadcrumb(breadcrumb);

const captureSentryMessage = (messageDetail: {
  message: string;
  level: 'info' | 'warning' | 'error';
  tags: { key: string; value: string }[];
  context: { key: string; detail: Record<string, unknown> };
}) => {
  Sentry.withScope((scope) => {
    messageDetail.tags.forEach(({ key, value }) => {
      scope.setTag(key, value);
    });
    scope.setLevel(messageDetail.level);
    scope.setContext(messageDetail.context.key, messageDetail.context.detail);
    Sentry.captureMessage(messageDetail.message);
  });
};

/** Absolute URLs are presigned, so their query carries a signature. */
const redactUrl = (url: string | undefined): string => {
  if (!url) {
    return 'UNKNOWN';
  }
  try {
    const { origin, pathname } = new URL(url);
    return `${origin}${pathname}`;
  } catch {
    return url;
  }
};

const captureHttpError = (error: AxiosError) => {
  const status = error.response?.status || 0;

  if (status < 400) {
    return;
  }

  const url = redactUrl(error.config?.url);

  Sentry.withScope((scope) => {
    scope.setTag('error.type', 'http');
    scope.setTag('http.status_code', status);
    scope.setTag(
      'http.method',
      error.config?.method?.toUpperCase() || 'UNKNOWN'
    );
    scope.setTag('http.url', url);
    if (status >= 500) {
      scope.setLevel('error');
    } else {
      scope.setLevel('warning');
    }

    scope.setContext('HTTP Request', {
      method: error.config?.method?.toUpperCase(),
      url,
      baseURL: error.config?.baseURL,
      status,
      statusText: error.response?.statusText,
    });

    Sentry.captureException(error);
  });
};

export {
  addSentryBreadcrumb,
  captureHttpError,
  captureSentryMessage,
  createBrowserRouterWithSentry,
  isTransientIndexedDBError,
  setErrorCaptureUser,
  setupErrorCapture,
};
